<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { errorText } from './api.js';

  export let api;
  const dispatch = createEventDispatcher();
  const states = { review: 'Reikia patikros', pending: 'Laukia apdorojimo', processing: 'Apdorojama', processed: 'Apdorota', purged: 'Turinys pašalintas' };
  const issues = {
    unmatched_channel_account: 'Nepavyko nustatyti kanalo paskyros',
    provider_identity_conflict: 'Tiekėjo žinutės tapatybės nesutampa',
    provider_recipient_conflict: 'Gavėjo duomenys nesutampa',
    invalid_timestamp: 'Netinkamas įvykio laikas',
    future_timestamp: 'Įvykio laikas daugiau kaip 5 min. vėlesnis už gavimo laiką. Reikalinga tiekėjo ir serverio laikrodžių patikra.',
    unsupported_event: 'Šis įvykio tipas dar neapdorojamas',
    unsupported_status: 'Neatpažinta tiekėjo būsena',
    invalid_content: 'Netinkama turinio struktūra', invalid_event: 'Netinkama įvykio struktūra',
    unsupported_provider: 'Nepalaikomas tiekėjas', projection_interrupted: 'Apdorojimas nutrūko',
    receipt_content_purged: 'Įvykio turinys pašalintas',
    history_source_missing: 'Istorijos šaltinio įrašo nebėra. Pakartojimas jo neatkurs. Kreipkitės į IT, kad trūkumas būtų užregistruotas istorijos perkėlimo patikroje.',
    history_content_conflict: 'Išsaugotas žinutės turinys nesutampa su istorijos šaltiniu. Turinys neperrašytas. Reikalinga IT patikra; pakartojimas neatitikimo neištaisys.',
  };
  let rows = [], nextBefore = null, snapshot = null, filter = 'review';
  let repairItems = [];
  let loading = true, selecting = false, saving = false, error = '', success = '', reason = '', confirmed = false;
  let alive = false, listGeneration = 0, selectionGeneration = 0, intent = null;
  $: ready = snapshot?.replay_allowed && !loading && !selecting && !saving && !!reason.trim() && confirmed;

  function report(problem) {
    if (!alive || problem.name === 'AbortError') return;
    error = problem.code || [401, 403].includes(problem.status) ? errorText(problem) : 'Nepavyko gauti atsakymo. Atnaujinkite būseną arba kartokite tą patį patikros veiksmą.';
    if ([401, 403].includes(problem.status)) dispatch('denied', problem);
  }

  async function load(append = false) {
    const generation = ++listGeneration;
    loading = true;
    const query = new URLSearchParams({ state: filter, limit: '25' });
    if (append && nextBefore) query.set('before', nextBefore);
    try {
      const result = await api(`receipts/?${query}`);
      if (!alive || generation !== listGeneration) return;
      rows = append ? [...new Map([...rows, ...result.results].map(row => [row.id, row])).values()] : result.results;
      nextBefore = result.next_before;
    } catch (problem) { report(problem); }
    finally { if (alive && generation === listGeneration) loading = false; }
  }

  async function inspect(id, preserveReason = false) {
    const generation = ++selectionGeneration;
    selecting = true; confirmed = false; repairItems = [];
    if (!preserveReason) { reason = ''; intent = null; error = ''; success = ''; snapshot = null; }
    try {
      const result = await api(`receipts/${id}/`);
      if (!alive || generation !== selectionGeneration) return;
      snapshot = result;
    } catch (problem) { report(problem); }
    finally { if (alive && generation === selectionGeneration) selecting = false; }
  }

  async function refresh() {
    error = '';
    await load();
    if (alive && snapshot) await inspect(snapshot.id, true);
  }

  async function submit() {
    if (!ready) return;
    saving = true; error = ''; success = '';
    const id = snapshot.id;
    const body = { expected_version: snapshot.version, reason: reason.trim() };
    if (repairItems.length) body.route_repairs = (snapshot.route_repairs || [])
      .filter(candidate => repairItems.includes(candidate.item))
      .map(({ item, account_id }) => ({ item, account_id }));
    const fingerprint = JSON.stringify({ id, ...body });
    if (intent?.fingerprint !== fingerprint) intent = { fingerprint, key: crypto.randomUUID() };
    try {
      await api(`receipts/${id}/replay/`, { method: 'POST', body: { ...body, idempotency_key: intent.key } });
      if (!alive) return;
      success = 'Pakartotinis apdorojimas suplanuotas. Vykdymui reikalingas veikiantis apdorojimo procesas.';
      confirmed = false;
      await inspect(id, true);
      await load();
      dispatch('changed');
    } catch (problem) {
      report(problem);
      if (alive && ['receipt_review_conflict', 'receipt_replay_unavailable', 'receipt_content_purged', 'receipt_route_repair_invalid', 'history_source_missing'].includes(problem.code)) {
        await inspect(id, true);
      }
    } finally { if (alive) saving = false; }
  }

  function date(value) { return value ? new Date(value).toLocaleString('lt-LT') : '—'; }
  onMount(() => { alive = true; void load(); return () => { alive = false; }; });
</script>

<section class="receipt-review" aria-label="Gautų įvykių patikra">
  <header><h2>Gautų įvykių patikra</h2><button type="button" on:click={() => dispatch('close')}>Uždaryti įvykių patikrą</button></header>
  <p>Pakartotinai apdorojami išsaugoti tiekėjo duomenys, o ne siunčiamos žinutės klientui. Atkurti įrašai rodomi kaip istorija, be naujo atsakymo lango ar pranešimų telefonui.</p>
  {#if error}<p class="error" role="alert">{error}</p>{/if}
  {#if success}<p role="status">{success}</p>{/if}
  <div class="tools"><label>Įvykių būsena<select bind:value={filter} disabled={saving || loading} on:change={() => load()}>
    {#each Object.entries(states) as [value, label]}<option {value}>{label}</option>{/each}
  </select></label><button type="button" disabled={loading || saving || selecting} on:click={refresh}>Atnaujinti įvykius</button></div>
  <div class="review-columns">
    <div class="queue">
      {#if loading}<p role="status">Įkeliami įvykiai…</p>{/if}
      {#each rows as row (row.id)}
        <button class="row" type="button" disabled={saving || selecting} aria-label={`Peržiūrėti įvykį ${row.id}`} on:click={() => inspect(row.id)}>
          <strong>#{row.id} · {row.provider}</strong><span>{states[row.state] || row.state} · {date(row.received_at)}</span>
        </button>
      {:else}{#if !loading}<p>Pasirinktos būsenos įvykių nėra.</p>{/if}{/each}
      {#if nextBefore}<button type="button" disabled={loading || saving} on:click={() => load(true)}>Daugiau įvykių</button>{/if}
    </div>
    <div class="inspection">
      {#if selecting}<p role="status">Tikrinama įvykio būsena…</p>
      {:else if snapshot}
        <h3>Įvykis #{snapshot.id} · {snapshot.provider}</h3>
        <p>{states[snapshot.state] || snapshot.state} · Bandymų: {snapshot.attempts}</p>
        {#each snapshot.issues as issue}<p>{issue.item == null ? '' : `Dalis ${issue.item + 1}: `}{issues[issue.code] || 'Neatpažintas įvykis. Reikalinga IT patikra.'}</p>{/each}
        {#if snapshot.issue_count > snapshot.issues.length}<p>Rodoma {snapshot.issues.length} iš {snapshot.issue_count} problemų. Išsamiai patikrai kreipkitės į IT.</p>{/if}
        {#if snapshot.content_purged}<p>Įvykio turinys pašalintas. Pakartotinis apdorojimas negalimas.</p>{/if}
        {#if snapshot.replay_allowed}
          <form on:submit|preventDefault={submit}>
            {#if snapshot.route_repairs?.length}
              <fieldset disabled={saving}>
                <legend>Paskyros susiejimo pataisa</legend>
                <p>Pasirinkite tik patikrintą siuntėjo paskyrą. Pataisa išsaugoma audite; įprastas kartojimas paskyros nekeičia.</p>
                {#each snapshot.route_repairs as candidate (candidate.item)}
                  <label class="confirmation"><input type="checkbox" value={candidate.item} bind:group={repairItems} />Susieti {candidate.item + 1} dalį su {candidate.account_name}</label>
                {/each}
              </fieldset>
            {/if}
            <label>Pakartotinės patikros pagrindas<textarea bind:value={reason} maxlength="500" rows="2" disabled={saving} placeholder="Trumpa patikros nuoroda; be klientų duomenų"></textarea></label>
            <label class="confirmation"><input type="checkbox" bind:checked={confirmed} disabled={saving} />Patvirtinu, kad patikrinau priežastį ir galima pakartoti apdorojimą</label>
            <button type="submit" disabled={!ready}>{saving ? 'Registruojama…' : repairItems.length ? 'Susieti ir pakartoti apdorojimą' : 'Pakartoti apdorojimą'}</button>
          </form>
        {/if}
        {#if snapshot.recent_replays?.length}
          <details><summary>Naujausi patikros sprendimai</summary><ul>
            {#each snapshot.recent_replays as event (event.id)}<li>{date(event.created_at)} · {event.actor?.name || 'Buvęs darbuotojas'}<p>{event.reason}</p></li>{/each}
          </ul>{#if snapshot.older_replays_exist}<p>Rodomi paskutiniai 20 sprendimų. Senesnius įrašus gali patikrinti IT.</p>{/if}</details>
        {/if}
      {:else}<p>Pasirinkite įvykį patikrai. Kliento žinutės turinys čia nerodomas.</p>{/if}
    </div>
  </div>
</section>

<style>
  .receipt-review{padding:16px 24px;border-bottom:1px solid #e8d2bb;background:#fff7ef;max-height:55vh;overflow:auto;font:12px/1.5 Inter,system-ui,sans-serif;flex-shrink:0;color:#25231f}
  header,.tools{display:flex;align-items:center;justify-content:space-between;gap:12px}h2,h3{margin:0;font-size:15px}p{margin:10px 0}.tools{justify-content:flex-start;margin-bottom:12px}.review-columns{display:grid;grid-template-columns:minmax(180px,1fr) minmax(260px,2fr);gap:20px}.queue{display:flex;flex-direction:column;gap:6px}.row{display:flex;flex-direction:column;gap:4px;text-align:left}.row span{font-size:11px}.inspection{min-width:0;overflow-wrap:anywhere}
  button,input,textarea,select{font:inherit;border:1px solid #bfb7a8;border-radius:6px;padding:8px;background:#fff;color:#25231f;box-sizing:border-box}button{cursor:pointer}button:disabled{opacity:.5;cursor:not-allowed}label{display:flex;flex-direction:column;gap:5px}form{display:flex;flex-direction:column;gap:10px}textarea{width:100%;resize:vertical}.confirmation{flex-direction:row;align-items:center}.confirmation input{width:18px;height:18px;flex-shrink:0}form>button{align-self:flex-start;background:#ba4510;color:#fff}.error{color:#922c19}details{margin-top:12px}summary{cursor:pointer}:focus-visible{outline:2px solid #d95513;outline-offset:2px}
  @media(max-width:760px){.receipt-review{padding:12px;flex:1;min-height:0;max-height:none}.review-columns{grid-template-columns:1fr}.queue{max-height:140px;overflow:auto}input,textarea,select{font-size:16px}}
</style>
